'use client'
import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io'
import Slider, { Settings } from 'react-slick'

import islamicLogo from '@/assets/images/islamic-logo.png'
import { PublicVideoDto } from '@/server/public-api/types'

const toEmbedUrl = (link: string | null): string | null => {
  if (!link) return null
  const match = link.match(/(?:youtu\.be\/|v=|embed\/)([^?&/]+)/)
  const id = match?.[1] ?? null
  return id ? `https://www.youtube.com/embed/${id}` : null
}

const VideosSection = ({ videos }: { videos: PublicVideoDto[] }) => {
  const slick = useRef<Slider | null>(null)
  const { t } = useTranslation()

  const next = () => slick.current?.slickNext()
  const previous = () => slick.current?.slickPrev()
  const stop = () => slick.current?.slickPause()

  useEffect(() => {
    const stopCarousel = () => {
      if (document.activeElement && document.activeElement.tagName === 'IFRAME') {
        slick.current?.slickPause()
      }
    }
    window.addEventListener('blur', stopCarousel)
    return () => window.removeEventListener('blur', stopCarousel)
  }, [])

  const validVideos = videos.filter(v => toEmbedUrl(v.link))
  if (validVideos.length === 0) return null

  const settings: Settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    draggable: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 5000,
  }

  return (
    <section className='max-sm:bg-secondary'>
      <div className='container bg-secondary py-4  text-center font-semibold md:bg-[#70827f]'>
        <h1>{t('vedios.title')}</h1>
      </div>
      <div className='bg-secondary'>
        <section className='relative md:bg-secondary'>
          <div className='container flex flex-col-reverse justify-between gap-8 md:flex-row md:py-14 '>
            <figure className='mx-auto md:mx-0'>
              <Image
                src={islamicLogo}
                alt='islamic-logo'
                loading='lazy'
                placeholder='blur'
                className='size-[100px] md:size-[450px]'
              />
            </figure>
            <div className=' relative md:w-1/2' role='button' tabIndex={0} onClick={stop} onKeyDown={stop}>
              <div className=''>
                <button
                  className='absolute start-5 top-1/2 z-50 size-10 translate-y-1/2  cursor-pointer rounded-full bg-secondary p-2 rtl:rotate-180'
                  title={t('videos.button.previous')}
                  aria-label={t('videos.button.previous')}
                  onClick={previous}
                >
                  <IoIosArrowBack className=' text-2xl' />
                </button>
                <button
                  className='absolute end-5 top-1/2 z-50 size-10 translate-y-1/2  cursor-pointer rounded-full bg-secondary p-2 rtl:rotate-180'
                  title={t('videos.button.next')}
                  aria-label={t('videos.button.next')}
                  onClick={next}
                >
                  <IoIosArrowForward className=' text-2xl' />
                </button>
              </div>
              <Slider {...settings} ref={slick}>
                {validVideos.map(video => (
                  <div key={video.id} className='relative'>
                    <p className='mb-6 text-center text-2xl font-semibold md:text-3xl '>{video.title}</p>
                    <iframe
                      allowFullScreen
                      className='block aspect-video w-full rounded-md'
                      src={toEmbedUrl(video.link)!}
                      title={t('videos.player.title')}
                      allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                      referrerPolicy='strict-origin-when-cross-origin'
                    />
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </section>
      </div>
    </section>
  )
}

export default VideosSection
